import { T, useT } from "@transifex/react";
import { router, useLocalSearchParams } from "expo-router";
import { useState } from "react";

import { useAuth } from "@/hooks/useAuth";
import { fetchPlate } from "@/services/plate.queries";
import { saveToStorage } from "@/utils/secureStore";

import Parking from "@/assets/icons/Parking";
import Ticket from "@/assets/icons/Ticket";

import ScanIllustration from "@/assets/illustrations/ScanIllustration";
import Screen from "@/components/layout/Screen";
import { ActiveFlexPassCard } from "@/components/shared/ActiveFlexPassCard";
import { ActiveSessionCard } from "@/components/shared/ActiveSessionCard";
import AppButton from "@/components/shared/AppButton";
import { Card } from "@/components/shared/Card";
import { GarageHeader, GarageHeaderSkeleton } from "@/components/shared/GarageHeader";
import ShowAddressModal from "@/components/shared/ShowAddressModal";
import { Box } from "@/components/ui/box";
import { Pressable } from "@/components/ui/pressable";
import { Skeleton, SkeletonText } from "@/components/ui/skeleton";
import { Text } from "@/components/ui/text";
import { VStack } from "@/components/ui/vstack";
import { useCustomTheme } from "@/hooks/useCustomTheme";
import useWelcome from "@/hooks/useWelcome";
import { getPhoneDigits } from "@/utils/formatPhoneNumber";
import { UIModule, WelcomeFailReason } from "ipctg-inflow-types/InFlowEnums";

interface UserActionsProps {
  hasUser: boolean;
  hasFlexPass: boolean;
}

const errorMessages: Record<string, string> = {
  Unauthorized: "You are not authorized to access this service.",
  SystemError: "There was an issue with the system. Please try again later.",
  InvalidRequestFormat: "The request format is invalid. Please check the data.",
  QRNotFound: "QR Code not found. Please verify the QR Code and try again.",
  QRNotActive: "QR Code is not active. Please contact support.",
};

const NoQrScreen = (props: { error: Error | null }) => {
  const t = useT();
  const failReason = props.error && "failReason" in props.error ? (props.error.failReason as string) : null;
  return (
    <Screen>
      <Box className="flex-1 justify-center items-center">
        {!props.error || failReason === WelcomeFailReason.QRNotFound ? (
          <VStack className="gap-5">
            <Text className="text-typography-900 text-2xl font-bold  leading-[29.05px] text-center">
              <T _str={"Get Started"} />
            </Text>
            <Text className="text-typography-900 text-sm leading-[20px] text-center mb-8">
              <T _str={"Open your phone’s camera app to scan your parking lot’s QR Code to get started"} />
            </Text>
            <ScanIllustration />
          </VStack>
        ) : (
          <Text className="text-typography-900 text-sm leading-[20px] text-center mb-8">
            {failReason ? t(errorMessages[failReason]) : t("An unknown error occurred.")}
          </Text>
        )}
      </Box>
    </Screen>
  );
};

const UserActions = ({ hasUser, hasFlexPass }: UserActionsProps) => {
  const t = useT();
  return (
    <>
      {hasUser ? (
        <Card className="gap-[24px] pt-[32px] pb-[24px] px-[20px]">
          <Text className="text-typography-900 text-2xl font-bold  leading-[29.05px] text-center">
            <T _str="Welcome" />
          </Text>
          <AppButton onPress={() => router.push("/purchase/singlepass")} title={t("Buy Parking")} icon={Parking} />
          {hasFlexPass && <AppButton onPress={() => router.push("/purchase/flex-park")} title={t("Buy FlexPark")} icon={Ticket} />}
        </Card>
      ) : (
        <>
          <Card className="gap-[24px] pt-[32px] pb-[24px] px-[20px]">
            <Text className="text-typography-900 text-2xl font-bold  leading-[29.05px] text-center">
              <T _str="Welcome" />
            </Text>
            <AppButton onPress={() => router.push("/purchase/singlepass")} title={t("Buy Parking")} icon={Parking} />
            {hasFlexPass && <AppButton onPress={() => router.push("/purchase/flex-park")} title={t("Buy FlexPark")} icon={Ticket} />}
          </Card>

          {hasFlexPass && (
            <Pressable onPress={() => router.push("/auth/sign-in")}>
              <Text className="text-typography-900 text-sm leading-[20px] text-center">
                <T _str="Already a FlexPark Member?" />{" "}
                <Text className="text-sm font-bold underline decoration-solid text-typography-900">
                  <T _str="Sign In" />
                </Text>{" "}
                <T _str="Now." />
              </Text>
            </Pressable>
          )}
        </>
      )}
    </>
  );
};

const WelcomeSkeleton = () => (
  <Screen>
    <GarageHeaderSkeleton />
    <VStack className="gap-5">
      <Card className="gap-[24px] pt-[32px] pb-[24px] px-[20px] items-center">
        <SkeletonText className="h-[30px] w-[150px] rounded-md" />
        <Skeleton className="h-[52px] w-full rounded-lg" />
        <Skeleton className="h-[52px] w-full rounded-lg" />
      </Card>
    </VStack>
  </Screen>
);

export default function Welcome() {
  const { qr } = useLocalSearchParams<{ qr?: string }>();
  const [isExtendLoading, setIsExtendLoading] = useState(false);
  const [isAddressModalVisible, setIsAddressModalVisible] = useState(false);
  const { data, error, isFetching: isWelcomeFetching } = useWelcome({ qrLookupID: qr || "" });
  const { skin } = useCustomTheme();
  const { user, isLoading: isAuthLoading } = useAuth();
  const t = useT();

  const hasFlexPass = data?.welcomeRsp.uiModules?.includes(UIModule.FlexPass) ?? false;
  const hasSinglePass = data?.welcomeRsp.uiModules?.includes(UIModule.SinglePurchase) ?? false;

  if (isAuthLoading || isWelcomeFetching || isExtendLoading) {
    return <WelcomeSkeleton />;
  }

  if (error || !data) {
    return <NoQrScreen error={error} />;
  }

  const handleExtendSession = async (sessionID: string, sessionPlate: any) => {
    try {
      setIsExtendLoading(true);
      if (sessionPlate) {
        await fetchPlate({ plate: sessionPlate }).catch((error) => console.error("Error fetching plate:", error));
      }

      if (user?.phone) {
        await saveToStorage("contact", `+${getPhoneDigits(user.phone)}`);
      }

      router.push({
        pathname: "/selectrate",
        params: {
          canExtend: "true",
          sessionID: sessionID,
        },
      });
    } catch (error) {
      console.error("Error processing session extension:", error);
    } finally {
      setIsExtendLoading(false);
    }
  };

  const { sessionsRsp, validPassesRsp } = data;

  return (
    <Screen>
      {skin && <GarageHeader locationName={skin.homepageHeader} onInfoPress={() => setIsAddressModalVisible(true)} />}
      <VStack className="gap-5">
        {(sessionsRsp?.parkingSessions.length || validPassesRsp) && (
          <VStack className="gap-5">
            {hasSinglePass &&
              sessionsRsp?.parkingSessions &&
              sessionsRsp.parkingSessions.length > 0 &&
              sessionsRsp.parkingSessions.map(({ session, canExtend }) => (
                <ActiveSessionCard
                  key={session.sessionID}
                  session={session}
                  canExtend={canExtend}
                  onExtend={(sessionID) => handleExtendSession(sessionID, session.plate)}
                />
              ))}
            {hasFlexPass &&
              validPassesRsp &&
              validPassesRsp.passes.length > 0 &&
              validPassesRsp.passes.map((pass) => (
                <ActiveFlexPassCard
                  key={pass.passID}
                  name={pass.passName}
                  usedPasses={pass.uses}
                  maxPassUses={pass.maxUses}
                  passNotes={pass.passNotes}
                  passID={pass.passID}
                />
              ))}
          </VStack>
        )}
        <UserActions hasUser={!!user} hasFlexPass={hasFlexPass} />
      </VStack>

      <ShowAddressModal
        isOpen={isAddressModalVisible}
        onClose={() => setIsAddressModalVisible(false)}
        locationName={skin?.locationName ?? t("Unknown location")}
        locationAddress={skin?.locationAddress?.address ?? t("Address not available")}
      />
    </Screen>
  );
}
